import React, { useEffect, useState } from "react";
import { useTable, useSortBy, Column } from "react-table";
import { useDispatch, useSelector } from "react-redux";
import { CellSortIcon } from "../../icons/sort";
import { backend, GroupListData } from "../../services";
import { formatDateTimeStandard, generateRandomString, sortGroupsHierarchy } from "../../utils";
import { toast } from "sonner";
import { actions, RootState } from "../../state/reducer";
import FileSymbol from "../../components/ui/fileSymbol";
import { closeModal, openModal } from "../../state/modalSlice";
import GroupAddUserModal from "../../modals/group/editGroupUser";
import DeleteGroupModal from "../../modals/group/deleteGroup";

interface GroupTableProps {
  columns: Column<GroupListData>[];
  data: GroupListData[];
}

const tableGridTemplate = "1fr 2fr 2fr 2fr 2fr 2fr";

const GroupsTable: React.FC<GroupTableProps> = ({ columns, data }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable<GroupTableProps>({ columns, data }, useSortBy);

  return (
    <div {...getTableProps()} style={{ display: "grid", gap: "10px" }}>
      {/* Заголовок таблицы */}
      <div
        className="tableHeading"
        style={{
          display: "grid",
          gridTemplateColumns: tableGridTemplate,
        }}
      >
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) => (
            <div
              {...column.getHeaderProps(column.getSortByToggleProps())}
              style={{ cursor: column.canSort ? "pointer" : "default" }}
            >
              {column.render("Header")}
              {console.log(typeof column.Header)}
              {column.Header !== "" && typeof column.Header === "string" ? (
                <CellSortIcon
                  width={8}
                  height={5}
                  classNames={
                    column.isSortedDesc
                      ? ["sortedDesc", "grayFilled"]
                      : ["grayFilled"]
                  }
                />
              ) : null}
            </div>
          ))
        )}
      </div>

      {/* Тело таблицы */}
      <div {...getTableBodyProps()} style={{ display: "grid", gap: "10px" }}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <div
              {...row.getRowProps()}
              style={{
                display: "grid",
                gridTemplateColumns: tableGridTemplate,
                padding: "10px 0",
                borderBottom: "1px solid #ccc",
              }}
            >
              {row.cells.map((cell: any) => (
                <div {...cell.getCellProps()}>{cell.render("Cell")}</div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GroupListTable = (props: { search: string }) => {
  const State = useSelector((state: RootState) => state);
  const dispatch = useDispatch();
  const [groupData, setGroupData] = useState<GroupListData[]>([]);

  useEffect(() => {
    backend
      .getGroupsList()
      .then((res) => {
        const sortedGroups = sortGroupsHierarchy(res.groups);
        setGroupData(sortedGroups);
      })
      .catch((e) => {
        toast("Не удалось загрузить список групп");
      });
  }, [State.selectedItem]);

  /* {
      name: "Отдел 7",
      date_created: "2020-10-10",
      date_activity: "2023-07-01",
      group: "Отдел 7",
      users_count: 9,
      watcher_internal_role: "Региональный директор",
    } */

  const columns: Column<GroupListData>[] = [
    {
      Header: "",
      accessor: "label",
      canSort: false,
      Cell: ({ row }: any) => {
        const level = row.original.hierarchy_level;
        return (
          <div className="flex gap6 vertCenter">
            {(() => {
              const symbols: any[] = [];
              for (let j = 0; j < level; j++) {
                symbols.push(<div className="groundSymbol" />);
              }
              return symbols;
            })()}
            <FileSymbol template={level ? "gray" : "blue"} />
          </div>
        );
      },
    },
    { Header: "Название", accessor: "name", canSort: true },
    {
      Header: "Дата создания",
      accessor: "date_created",
      Cell: ({ row }: any) => {
        return row.original.date_created ? (
          <div className={`userRoleTableText blue`}>
            {formatDateTimeStandard(row.original.date_created)}
          </div>
        ) : null;
      },
    },
    { Header: "Участники", accessor: "members" },
    {
      Header: "Роль",
      accessor: "watcher_internal_role_id",
      Cell: ({ row }: any) => {
        const role = row.original.watcher_internal_role_id;
        return role ? (
          <div className={`userRoleTableText ${role === 1 ? "green" : "blue"}`}>
            {role === 1 ? "Владелец" : "Участник"}
          </div>
        ) : null;
      },
    },
    {
      Header: "Действие",
      accessor: "action",
      Cell: ({ row }: any) => {
        const handleOpenGroupUsersModal = () => {
          dispatch(
            openModal({
              component: GroupAddUserModal,
              context: {
                group_id: row.original.id,
                onSave: () => {
                  dispatch(actions.selectItem(generateRandomString(3)));
                },
                closeModal: () => dispatch(closeModal()), // Закрыть модальное окно через Redux
              },
            })
          );
        };

        const handleOpenDeleteModal = () => {
          dispatch(
            openModal({
              component: DeleteGroupModal,
              context: {
                group_id: row.original.id,
                group_name: row.original.name,
                onSave: () => {
                  dispatch(actions.selectItem(generateRandomString(3)));
                },
                closeModal: () => dispatch(closeModal()), // Закрыть модальное окно через Redux
              },
            })
          );
        };

        // DeleteGroupModal

        return (
          <div className="flex gap6">
            <button
              className="filterButton tableActionButton"
              type="button"
              onClick={handleOpenGroupUsersModal}
            >
              <span>Участники</span>
            </button>
            <button
              className="filterButton tableActionButton red"
              type="button"
              onClick={handleOpenDeleteModal}
            >
              <span>Удалить</span>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="tableContainer">
      <GroupsTable data={groupData} columns={columns} />
      {/* <Paginator /> */}
    </div>
  );
};

export default GroupListTable;
